import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  isHolidayShippingEnabled,
  hasExpired,
  timeRemaining,
  getFormattedCutoffDate,
} from "../store/holiday-store";

/**
 * Holiday Shipping Countdown Component
 *
 * Displays a countdown timer until the holiday shipping cutoff date.
 * After expiration, shows a notice that holiday shipping is not guaranteed.
 *
 * Features:
 * - Reactive to holiday-store signals
 * - Countdown: days, hours, minutes
 * - Expired notice with icon
 * - Info button to open modal with shipping details
 * - Fully styled with Tailwind
 *
 * Usage in Liquid:
 * <holiday-shipping-countdown
 *   data-info-text="Orders placed by {date} will arrive before the holidays."
 *   data-expired-text="Orders placed now may not arrive before the holidays."
 * ></holiday-shipping-countdown>
 */
@customElement("holiday-shipping-countdown")
export class HolidayShippingCountdown extends LitElement {
  @property({ attribute: "data-info-text" }) infoText =
    "Orders placed by {date} will arrive before the holidays.";
  @property({ attribute: "data-expired-text" }) expiredText =
    "Orders placed now may not arrive before the holidays. Consider purchasing a gift card.";

  @state() private _isEnabled = false;
  @state() private _hasExpired = false;
  @state() private _timeRemaining: { days: number; hours: number; minutes: number } | null = null;
  @state() private _showInfoModal = false;
  @state() private _showExpiredModal = false;

  constructor() {
    super();

    // Subscribe to holiday store signals
    effect(() => {
      this._isEnabled = isHolidayShippingEnabled.value;
      this._hasExpired = hasExpired.value;
      this._timeRemaining = timeRemaining.value;
    });
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this; // Light DOM for Tailwind
  }

  private _openInfoModal() {
    this._showInfoModal = true;
  }

  private _closeInfoModal() {
    this._showInfoModal = false;
  }

  private _openExpiredModal() {
    this._showExpiredModal = true;
  }

  private _closeExpiredModal() {
    this._showExpiredModal = false;
  }

  private _renderCountdown() {
    const time = this._timeRemaining;
    if (!time) return html``;

    return html`
      <div class="holiday-shipping-countdown bg-[#FFEBD3] py-3 px-4 rounded-lg mb-4">
        <div class="flex items-center gap-3">
          <!-- Gift Icon -->
          <div class="flex-shrink-0">
            <svg class="w-8 h-8 text-[#913200]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>

          <!-- Countdown Content -->
          <div class="flex-1">
            <div class="text-sm font-bold text-[#913200] mb-1">
              Order by ${getFormattedCutoffDate()} for holiday shipping!
            </div>
            <div class="flex items-center gap-4 text-xs text-[#913200]">
              ${time.days > 0 ? html`<div class="font-medium">${time.days} <span class="font-normal">day${time.days !== 1 ? "s" : ""}</span></div>` : ""}
              ${time.hours > 0 ? html`<div class="font-medium">${time.hours} <span class="font-normal">hour${time.hours !== 1 ? "s" : ""}</span></div>` : ""}
              <div class="font-medium">${time.minutes} <span class="font-normal">minute${time.minutes !== 1 ? "s" : ""}</span></div>
            </div>
          </div>

          <!-- Info Button -->
          <button
            @click=${this._openInfoModal}
            class="flex-shrink-0 text-[#913200] hover:text-[#7a2a00] transition-colors"
            aria-label="Holiday shipping information"
            type="button"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <!-- Info Modal -->
        ${this._showInfoModal ? this._renderInfoModal() : ""}
      </div>
    `;
  }

  private _renderExpiredNotice() {
    return html`
      <div class="holiday-shipping-expired bg-red-50 border border-red-200 py-3 px-4 rounded-lg mb-4">
        <div class="flex items-center gap-3">
          <!-- Warning Icon -->
          <div class="flex-shrink-0">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          <!-- Content -->
          <div class="flex-1">
            <div class="text-sm font-bold text-red-800 mb-1">
              Holiday Shipping Deadline Passed
            </div>
            <div class="text-xs text-red-700">
              Orders may not arrive before the holidays.
            </div>
          </div>

          <!-- Info Button -->
          <button
            @click=${this._openExpiredModal}
            class="flex-shrink-0 text-red-600 hover:text-red-800 transition-colors"
            aria-label="More information"
            type="button"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <!-- Expired Info Modal -->
        ${this._showExpiredModal ? this._renderExpiredInfoModal() : ""}
      </div>
    `;
  }

  private _renderInfoModal() {
    return html`
      <div
        class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        @click=${this._closeInfoModal}
      >
        <div
          class="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          @click=${(e: Event) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div class="flex items-start justify-between mb-4">
            <h2 class="text-lg font-bold text-gray-900">Holiday Shipping Information</h2>
            <button
              @click=${this._closeInfoModal}
              class="text-gray-400 hover:text-gray-600"
              aria-label="Close"
              type="button"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="text-sm text-gray-700 mb-4">
            ${this.infoText.replace("{date}", getFormattedCutoffDate() || "")}
          </div>

          <div class="text-xs text-gray-500 mb-6">
            <p class="mb-2">Please note:</p>
            <ul class="list-disc list-inside space-y-1">
              <li>Shipping times may vary by location</li>
              <li>Carrier delays may occur during holidays</li>
              <li>Order early to ensure delivery</li>
            </ul>
          </div>

          <button
            @click=${this._closeInfoModal}
            class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            type="button"
          >
            Got It
          </button>
        </div>
      </div>
    `;
  }

  private _renderExpiredInfoModal() {
    return html`
      <div
        class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        @click=${this._closeExpiredModal}
      >
        <div
          class="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          @click=${(e: Event) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div class="flex items-start justify-between mb-4">
            <h2 class="text-lg font-bold text-gray-900">Holiday Shipping Deadline Passed</h2>
            <button
              @click=${this._closeExpiredModal}
              class="text-gray-400 hover:text-gray-600"
              aria-label="Close"
              type="button"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="text-sm text-gray-700 mb-4">
            ${this.expiredText}
          </div>

          <div class="text-xs text-gray-500 mb-6">
            <p class="mb-2">Alternative options:</p>
            <ul class="list-disc list-inside space-y-1">
              <li>Purchase a gift card for instant delivery</li>
              <li>Consider digital gift options</li>
              <li>Standard shipping still available</li>
            </ul>
          </div>

          <button
            @click=${this._closeExpiredModal}
            class="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    `;
  }

  render() {
    // Don't render if feature is disabled
    if (!this._isEnabled) {
      return html``;
    }

    // Don't render if no cutoff date set
    if (!this._timeRemaining && !this._hasExpired) {
      return html``;
    }

    return this._hasExpired ? this._renderExpiredNotice() : this._renderCountdown();
  }
}
